import React, { useRef, useEffect } from 'react';
import AOS from 'aos';
import '../styles/pages/RRMS.css';
import Navbar from '../components/layout/Navbar';

// Import images (you'll need to add these to the Assets folder)
import Banner from '../components/assets/RRMS/Banner.png';
import Homepage_old from '../components/assets/RRMS/homepage_old.gif';
import Homepage_new from '../components/assets/RRMS/homepage_new.gif';
import Dispatch_request_old from '../components/assets/RRMS/dispatch_request_old.gif';
import Dispatch_request_new from '../components/assets/RRMS/dispatch_request_new.gif';
import Cancellation_old from '../components/assets/RRMS/cancellation_old.gif';
import Cancellation_new from '../components/assets/RRMS/cancellation_new.gif';
import Dispatch_after_cancellation_old from '../components/assets/RRMS/dispatch_after_cancellation_old.gif';
import Dispatch_after_cancellation_new from '../components/assets/RRMS/dispatch_after_cancellation_new.gif';
import Chat_old from '../components/assets/RRMS/chat_old.gif';
import Chat_new from '../components/assets/RRMS/chat_new.gif';
import Alarm_history_old from '../components/assets/RRMS/alarm_history_old.gif';
import Alarm_history_new from '../components/assets/RRMS/alarm_history_new.gif';
import Disregard_old from '../components/assets/RRMS/disregard_old.gif';
import Disregard_new from '../components/assets/RRMS/disregard_new.gif';
import Feedback from '../components/assets/RRMS/Feedback.gif';
import Information_architecture from '../components/assets/RRMS/Information_architecture.png';


// Placeholder for missing images - replace these with actual images when available
const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlIFBsYWNlaG9sZGVyPC90ZXh0Pgo8L3N2Zz4K';

const sectionCount = 12; // Overview, Goals, Problems, What Next, 7 comparison sections, Bonus, Learnings

const RRMS = () => {
  const progressRef = useRef(null);
  const progressMobileRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.rrms-content section, .rrms-content > div[data-aos]');
      let viewed = 0;
      const buffer = window.innerHeight * 0.3;
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight - buffer) viewed++;
      });
      const percent = Math.max(0, Math.min(1, viewed / sectionCount));
      if (progressRef.current) {
        progressRef.current.style.height = `${percent * 100}%`;
      }
      if (progressMobileRef.current) {
        if (window.innerWidth <= 700) {
          progressMobileRef.current.style.width = `${percent * 100}%`;
        }
      }
    };
    
    // Prevent automatic scrolling on page load
    window.scrollTo(0, 0);
    
    window.addEventListener('scroll', handleScroll);
    // Delay the initial handleScroll call to prevent immediate scroll behavior
    setTimeout(() => {
      handleScroll();
    }, 100);
    
    AOS.refresh();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    AOS.init({ once: false, duration: 700, easing: 'ease-out-cubic' });
  }, []);

  const comparisonSections = [
    {
      title: "Home page",
      mainDescription: "The homepage has been modified to feature a design that is more modular and emphasises visual hierarchy. The intention was to introduce structure by utilising primary and secondary calls-to-action (CTA) and dividing the content accordingly.",
      oldGif: Homepage_old,
      newGif: Homepage_new,
      oldDescription: "Original home page interface",
      newDescription: "Redesigned home page with improved navigation"
    },
    {
      title: "Dispatch request",
      mainDescription: "The initial Dispatch request involved a cumbersome 9-step process. During emergencies, users must be able to easily monitor the system's status. The redesign streamlines this into a concise 5-step process, minimising clicks for users. Additionally, the redesign offers users the flexibility to input additional information if needed.",
      oldGif: Dispatch_request_old,
      newGif: Dispatch_request_new,
      oldDescription: "Previous dispatch request flow",
      newDescription: "Streamlined dispatch request process"
    },
    {
      title: "Cancellation",
      mainDescription: "The cancellation was evident in the previous design. Nevertheless, it has been restructured to align with the overall redesign. The CTA button has been given secondary priority to highlight the dispatch option in case of emergencies.",
      oldGif: Cancellation_old,
      newGif: Cancellation_new,
      oldDescription: "Original cancellation interface",
      newDescription: "Improved cancellation workflow"
    },
    {
      title: "Dispatch after cancellation",
      mainDescription: "This feature has been my favorite within the app. It's incredibly helpful for users seeking to retrace their steps without penalizing them for mistakes. The content has been revised to ensure users can easily comprehend the required actions. Additionally, a link to the instructions for disarming the alarm is readily available to users.",
      oldGif: Dispatch_after_cancellation_old,
      newGif: Dispatch_after_cancellation_new,
      oldDescription: "Previous post-cancellation dispatch",
      newDescription: "Enhanced post-cancellation dispatch"
    },
    {
      title: "Chat",
      mainDescription: "The chat window has been reorganized to establish a clearer hierarchy on the screen. A distinct separation between the user's messages and those of other participants is now indicated using different colors. Additionally, the number of participants currently online is displayed alongside the participant count for enhanced visibility.",
      oldGif: Chat_old,
      newGif: Chat_new,
      oldDescription: "Original chat functionality",
      newDescription: "Redesigned chat interface"
    },
    {
      title: "Alarm History",
      mainDescription: "Previously, the alarm history solely showed the current alarm status. However, the updated design incorporates a new feature allowing users to log in and access past case statuses. A clear distinction is now present between ongoing and past cases, providing a marked division for easier navigation and comprehension.",
      oldGif: Alarm_history_old,
      newGif: Alarm_history_new,
      oldDescription: "Previous alarm history view",
      newDescription: "Improved alarm history display"
    },
    {
      title: "Disregard",
      mainDescription: "The disregard feature has been revamped to enable users to make decisions with minimal steps. Leveraging the available screen space more effectively allows for informed choices. Additionally, the new design enables users to pause or cancel the disregard action, providing greater control and flexibility within the feature.",
      oldGif: Disregard_old,
      newGif: Disregard_new,
      oldDescription: "Original disregard functionality",
      newDescription: "Enhanced disregard process"
    }
  ];

  return (
    <div className="rrms-page">
      <Navbar />
      {/* Progress indicator */}
      <div className="rrms-progress-indicator">
        <div ref={progressRef} style={{ width: '100%', height: 0 }}></div>
      </div>
      <div className="rrms-progress-indicator-mobile">
        <div ref={progressMobileRef} style={{ width: 0 }}></div>
      </div>

      {/* Banner */}
      <img src={Banner} alt="Rapid Response Monitoring Services Banner" className="rrms-header" data-aos="fade-up" />

      <div className="rrms-content">
        {/* Overview Section */}
        <section data-aos="fade-up" className="rrms-overview-section">
          <h2>OVERVIEW</h2>
          <p>Rapid Response is a reputable company specializing in comprehensive alarm monitoring and top-tier security services. Their expertise spans various security systems, encompassing burglar alarms, fire alarms, environmental monitoring, and an array of other security-related devices. Among their offerings is 'RapidSMS,' a highly adaptable and client-customizable web application designed explicitly for prompt burglary response.</p>
          <p>RapidSMS serves as a tailored solution crafted to aid users during critical emergencies. The current iteration of the solution exhibits a few shortcomings that could potentially lead to confusion in emergency scenarios. This Redesign seeks to address these flaws, aiming to enhance its functionality and user-friendliness, ensuring a more intuitive and efficient system for emergency response.</p>

          <div className="rrms-other-details-container">
            <div className="rrms-tools-section">
              <strong>Tool Used:</strong> Figma
            </div>

            <div className="rrms-tools-section">
              <strong>Duration:</strong> 2 Weeks
            </div>

            <div className="rrms-tools-section">
              <strong>Role:</strong> UX Research, UX Design, UI Design, UX Writing
            </div>
          </div>

        </section>

        {/* Goals Section */}
        <section data-aos="fade-up" className="rrms-goals-section">
          <h2>GOALS</h2>
          <ul>
            <li><strong>Ensure immediate accessibility:</strong> Enable users to respond quickly to alarms through a hyperlink embedded in the RapidSMS message, leading to an intuitive, interactive interface.</li>
            <li><strong>Streamline critical actions:</strong> Provide clear, push-button options for urgent decisions, such as:</li>
            <ul>
              <li>Cancelling false alarms</li>
              <li>Requesting emergency services</li>
              <li>Chatting with emergency contacts</li>
              <li>Contacting the monitoring center</li>
              <li>Sending service inquiries</li>
            </ul>
            <li><strong>Support brand consistency:</strong> Integrate flexible branding options so clients can maintain their identity within the system</li>
            <li><strong>Enable tech scalability:</strong> Create a modern, responsive interface that seamlessly integrates with legacy systems and adapts to various installed equipment.</li>
            <li><strong>Design for universality:</strong> Ensure the experience works for any professionally monitored account, regardless of hardware constraints.</li>
          </ul>
        </section>

        {/* Information Architecture */}
        <section data-aos="fade-up" className="rrms-problems-section">
          <h2>What's going wrong in the current design?</h2>
          <p>I first needed to understand how the app works — its core features and the structure of its information architecture. Once I had a clear picture, I noted down potential pain points that could frustrate users, along with opportunities for improvement (see dotted lines).</p>
          <img src={Information_architecture} alt="Information Architecture" className="rrms-architecture-img" />
        </section>

        {/* What Next Section */}
        <section data-aos="fade-up" className="rrms-what-next-section">
          <h2>UPDATED DESIGN:</h2>
          <ul>
            <li><strong>Improve discoverability and clarity:</strong> Make it easier for users to find the information they need.</li>
            <li><strong>Support brand consistency:</strong>Use a common replacable color system to ensure consistency across the app.</li>
            <li><strong>Improve visual hierarchy:</strong>Grouping identical elements together and using consistent spacing to create a more intuitive and user-friendly interface.</li>
            <li><strong>Improve usability:</strong> Make it easier for users to use the app by reducing cognitive load and improving the user flow.</li>
          </ul>
          <p>
            <strong>Let's update each page and discuss how it addresses the issues we identified.</strong>
          </p>
        </section>

        {/* Comparison Sections */}
        {comparisonSections.map((section, index) => (
          <section key={index} data-aos="fade-up" className="rrms-comparison-section">
            <div className="rrms-comparison-info-section">
              <h3>{section.title}</h3>
              <p>{section.mainDescription}</p>
            </div>
            <div className="rrms-comparison-grid">
              <div className="rrms-old-column">
                <h4>Before</h4>
                <img src={section.oldGif} alt={`${section.title} - Old`} />
                <p className="rrms-description">{section.oldDescription}</p>
              </div>

              <div className="rrms-arrow">
                <svg width="80" height="30" viewBox="0 0 80 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 15 C30 0, 50 30, 70 15"
                    stroke="#962D2E"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-dasharray="4,4"
                  />
                  <path
                    d="M78 15 L70 9 L70 21 Z"
                    fill="#962D2E"
                  />
                </svg>

              </div>

              <div className="rrms-new-column">
                <h4>After</h4>
                <img src={section.newGif} alt={`${section.title} - New`} />
                <p className="rrms-description">{section.newDescription}</p>
              </div>
            </div>
          </section>
        ))}


        {/* Bonus Section - Feedback */}
        <section data-aos="fade-up" className="rrms-bonus-section">
          <h2>SUGGESTED - FEEDBACK PAGE:</h2>
          <p>This feedback page demonstrates the enhanced user experience with improved form design, better visual hierarchy, and more intuitive interaction patterns. The redesign focuses on reducing cognitive load while maintaining functionality and accessibility.</p>

          <div className="rrms-bonus-grid">
            <img src={Feedback} alt="Feedback Screen" />
          </div>
        </section>

        {/* Learnings Section */}
        <section data-aos="fade-up" className="rrms-learnings-section">
          <h2>KEY LEARNINGS:</h2>
          <p>This project provided insight into the meticulous thinking behind the creation of this product. It was evidently a well-considered product, enabling me to understand why specific features were incorporated. However, the focus now shifts towards developing an architecture that not only enables users to fully leverage these features but also ensures a design that is more intuitive and easily understandable.</p>
          <p>Though the project primarily centered on UI, delving into the usability aspect led me to uncover several areas requiring further UX refinement. It became evident that a cross-functional team was crucial as certain features demanded clarification regarding their technical backend workings to ensure the design accurately represented their functionality.</p>
          <p>Overall, it was a small but effective project that made me truly explore the principles of UX Design, UI Design, Content Design and UX Research.</p>
        </section>
      </div>
    </div>
  );
};

export default RRMS; 